import {
  provideKeycloak,
  createInterceptorCondition,
  withAutoRefreshToken,
  AutoRefreshTokenService,
  UserActivityService,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  KEYCLOAK_EVENT_SIGNAL,
  KeycloakEventType
} from 'keycloak-angular';
import { effect, inject, provideAppInitializer } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../../../environments/environment';

const serverCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: new RegExp(`^${environment.serverPath.replace('/', '\\/')}.*`, 'i')
});

const keycloakConfig = {
  realm: environment.keycloak.realm,
  url: environment.keycloak.url,
  clientId: environment.keycloak.clientId
};

interface HandoffTokens {
  token: string;
  refreshToken: string;
}

/**
 * SSO vindo do Workspace: o token é entregue no fragment (#mc_sso=<base64 json>), que não vai ao
 * servidor nem ao referrer. Lê, remove o fragment da URL (não deixa token no history) e devolve os
 * tokens para inicializar o Keycloak já autenticado — sem tela de login.
 */
const readHandoffTokens = (): HandoffTokens | null => {
  const match = window.location.hash.match(/mc_sso=([^&]+)/);
  if (!match) return null;

  try {
    const sso = JSON.parse(atob(decodeURIComponent(match[1])));
    // Remove o fragment: token não pode persistir na URL/history.
    history.replaceState(null, '', window.location.pathname + window.location.search);

    if (!sso?.accessToken || !sso?.refreshToken) return null;
    return { token: sso.accessToken, refreshToken: sso.refreshToken };
  } catch {
    return null;
  }
};

// atob sozinho lê os bytes como Latin-1 — claims com acento (name: "Gonçalves") viravam mojibake
// ("GonÃ§alves") e persistiam assim na sessão. Decodifica os bytes como UTF-8, igual ao keycloak-js.
const decodeJwtPayload = (jwt: string) => {
  const base64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

/**
 * Adota os tokens do handoff direto na instância — equivalente ao setToken interno do keycloak-js.
 * O caminho nativo do init NÃO serve para token trocado (RFC 8693, azp=workspace):
 * - token+refreshToken com checkLoginIframe desligado → init força updateToken(-1), e o KC recusa
 *   renovar token de outro client ("Token client and authorized client don't match", 400) → login;
 * - token sozinho → init ignora (a adoção nativa exige o par) e segue deslogado.
 */
const adoptHandoffTokens = (keycloak: Keycloak, handoff: HandoffTokens): void => {
  const tokenParsed = decodeJwtPayload(handoff.token);

  keycloak.token = handoff.token;
  keycloak.tokenParsed = tokenParsed;
  keycloak.idTokenParsed = tokenParsed;
  // Mantido pelos checks internos: updateToken() só vai à rede perto da expiração — antes disso
  // resolve local, então o refresh recusável não é disparado. Quando o access token expirar, a
  // sessão acaba e o usuário reabre pelo card do Workspace (novo token-exchange).
  keycloak.refreshToken = handoff.refreshToken;
  keycloak.refreshTokenParsed = decodeJwtPayload(handoff.refreshToken);
  keycloak.authenticated = true;
  // Sem timeSkew, isTokenExpired() não calcula e reporta expirado → dispararia o refresh (400).
  // Token recém-emitido no clique: skew zero.
  keycloak.timeSkew = 0;
  keycloak.subject = tokenParsed.sub;
  keycloak.realmAccess = tokenParsed.realm_access;
  keycloak.resourceAccess = tokenParsed.resource_access;

  keycloak.onAuthSuccess?.();
};

// ── Persistência da sessão entre reloads/redeploys ─────────────────────────
// A recuperação nativa (check-sso) depende de iframe com cookie de TERCEIRO
// (front *.marketconnector.com.br × KC auth.sisand.com.br) — bloqueado pelos
// browsers atuais. Com tokens só em memória, todo full reload (inclusive o
// causado por redeploy) derrubava a sessão. O par access+refresh fica no
// localStorage e é readotado no boot; o refresh imediato do init valida no
// servidor (sessão revogada → par recusado → segue deslogado e limpa).
// Trade-off consciente: token em localStorage é legível por XSS — mesmo perfil
// de risco já aceito no handoff via fragment do Workspace.

const SESSION_STORAGE_KEY = 'mc_kc_session:v1';

interface StoredSession extends HandoffTokens {
  // Par vindo do token-exchange do Workspace (azp=workspace): o refresh nativo é
  // recusado pelo KC, então a readoção é manual e a validade é a do access token.
  handoff: boolean;
}

const saveSession = (keycloak: Keycloak, handoff: boolean): void => {
  if (!keycloak.token || !keycloak.refreshToken) return;
  try {
    localStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({ token: keycloak.token, refreshToken: keycloak.refreshToken, handoff })
    );
  } catch { /* storage indisponível: segue sem persistência (comportamento antigo) */ }
};

const clearStoredSession = (): void => {
  try { localStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* noop */ }
};

const readStoredSession = (): StoredSession | null => {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredSession;
    if (!stored?.token || !stored?.refreshToken) return null;
    // No fluxo normal quem reabre a sessão é o refresh token; no handoff ele não é
    // utilizável e vale a expiração do próprio access.
    const reference = stored.handoff ? stored.token : stored.refreshToken;
    const exp = decodeJwtPayload(reference)?.exp;
    if (!exp || exp * 1000 <= Date.now() + 5000) {
      clearStoredSession();
      return null;
    }
    return stored;
  } catch {
    clearStoredSession();
    return null;
  }
};

// Mantém o storage em dia pelos eventos do keycloak-angular: login e cada refresh
// regravam o par; logout (ou par recusado no boot) limpa.
const sessionPersistenceInitializer = (handoff: boolean) =>
  provideAppInitializer(() => {
    const keycloak = inject(Keycloak);
    const keycloakEvent = inject(KEYCLOAK_EVENT_SIGNAL);
    effect(() => {
      const event = keycloakEvent();
      switch (event.type) {
        case KeycloakEventType.Ready:
        case KeycloakEventType.AuthSuccess:
        case KeycloakEventType.AuthRefreshSuccess:
          if (keycloak.authenticated) saveSession(keycloak, handoff);
          break;
        case KeycloakEventType.AuthLogout:
          clearStoredSession();
          break;
        case KeycloakEventType.AuthRefreshError:
          // Refresh de rotina pode falhar por rede (sessão segue de pé) — só descarta
          // o par quando o adapter concluiu que não há mais sessão.
          if (!keycloak.authenticated) clearStoredSession();
          break;
      }
    });
  });

export const provideKeycloakAngular = () => {
  const handoff = readHandoffTokens();
  const stored = handoff ? null : readStoredSession();

  // Modo handoff (SSO do Workspace): SEM initOptions → o keycloak-angular não inicializa sozinho.
  // O init roda "vazio" (sem onLoad/check-sso — cross-domain não há cookie SSO e o silent-check
  // descartaria a sessão) e os tokens são adotados na mão logo em seguida, ainda no app initializer
  // (antes do router ativar guards/resolvers). Sem withAutoRefreshToken: o refresh de token trocado
  // é recusado pelo KC (azp=workspace) e derrubaria a sessão à toa.
  // O par do handoff também sobrevive a reload/redeploy: readotado do storage
  // enquanto o access token valer (o refresh dele não é renovável — azp=workspace).
  const handoffTokens = handoff ?? (stored?.handoff ? stored : null);

  if (handoffTokens) {
    return provideKeycloak({
      config: keycloakConfig,
      providers: [
        {
          provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
          useValue: [serverCondition]
        },
        provideAppInitializer(async () => {
          const keycloak = inject(Keycloak);
          await keycloak.init({ checkLoginIframe: false });
          adoptHandoffTokens(keycloak, handoffTokens);
          saveSession(keycloak, true);
        }),
        sessionPersistenceInitializer(true)
      ]
    });
  }

  return provideKeycloak({
    config: keycloakConfig,
    initOptions: {
      onLoad: 'check-sso',
      // Par persistido do load anterior: o init readota e dispara um refresh imediato
      // que VALIDA a sessão no servidor — recusado (logout/expirada), o adapter limpa
      // e o check-sso segue como fallback. Sem par salvo, comportamento de sempre.
      ...(stored ? { token: stored.token, refreshToken: stored.refreshToken } : {}),
      silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
      redirectUri: window.location.origin + '/home',
      // Cross-domain (front *.marketconnector.com.br × KC auth.sisand.com.br): o iframe de status
      // de login faz polling da sessão do KC via cookie 3rd-party — bloqueado por Safari/Firefox/
      // Chrome novo → gera erro no console e pode disparar logout espúrio. Desligado; a sessão é
      // verificada no load (check-sso) e renovada pelo withAutoRefreshToken.
      checkLoginIframe: false
    },
    features: [
      withAutoRefreshToken({
        // 'none' (era 'logout'): "mantenha-me conectado" NÃO pode derrubar a sessão por inatividade.
        // O token segue sendo renovado enquanto há atividade; ocioso, apenas para de renovar — a
        // sessão continua válida até o SSO/remember-me do Keycloak expirar, sem logout forçado no
        // client (que ainda por cima encerrava a sessão SSO e invalidava o remember-me).
        onInactivityTimeout: 'none',
        sessionTimeout: 900000
      })
    ],
    providers: [
      AutoRefreshTokenService,
      UserActivityService,
      {
        provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
        useValue: [serverCondition]
      },
      sessionPersistenceInitializer(false)
    ]
  });
};
